import { useEffect, useMemo, useRef, useState } from 'react';
import { Command } from 'cmdk';
import { useFbcPerformanceQuery, useFranchiseesQuery } from '../api/queries';
import { useAuth } from '../hooks/useAuth';
import { useSatellitePreference } from '../hooks/useSatellitePreference';
import { useTerritoryScope } from '../hooks/useTerritoryScope';
import type { ContextView, Franchisee } from '../types';

type Props = {
  view: ContextView;
  onChange: (view: ContextView) => void;
};

/**
 * Substring-based filter, slightly stricter than cmdk's default fuzzy match.
 *
 * - Each whitespace-separated token in `search` must appear as a substring in
 *   the item's value or one of its keywords (case-insensitive). Eliminates
 *   character-skip matches like "tco" → "Tacoma".
 * - If the full search string also appears as a contiguous substring,
 *   the item is ranked higher so "AF-101" or "san diego" sort to the top.
 */
function strictFilter(value: string, search: string, keywords?: string[]): number {
  const trimmed = search.trim().toLowerCase();
  if (!trimmed) return 1;
  const haystack = (value + ' ' + (keywords ?? []).join(' ')).toLowerCase();
  const tokens = trimmed.split(/\s+/);
  for (const t of tokens) {
    if (!haystack.includes(t)) return 0;
  }
  return haystack.includes(trimmed) ? 1 : 0.6;
}

function franchiseeLabel(f: Franchisee): string {
  const name =
    f.marketing_name ||
    f.territory_name ||
    [f.first_name, f.last_name].filter(Boolean).join(' ') ||
    '(unnamed)';
  return `${f.af_number} — ${name}`;
}

function viewLabel(view: ContextView, role: string | undefined, franchisees: Franchisee[]): string {
  if (view.kind === 'network') {
    return role === 'admin' ? '★ Network Overview' : '★ My Portfolio';
  }
  if (view.kind === 'fbc') {
    return role === 'fbc' ? '★ My Portfolio' : `${view.fbcName}'s Portfolio`;
  }
  // franchisee
  const f = franchisees.find((x) => x.af_number === view.afNumber);
  if (f) return franchiseeLabel(f);
  return view.afNumber;
}

export default function ContextPicker({ view, onChange }: Props) {
  const { user } = useAuth();
  const { data: franchisees, isLoading: fLoading } = useFranchiseesQuery();
  const { data: fbcPerf, isLoading: fbcLoading } = useFbcPerformanceQuery(
    '',
    '',
    user?.role === 'admin',
  );

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [showSatellites, setShowSatellites] = useSatellitePreference();
  const [onlyMyTerritories, setOnlyMyTerritories] = useTerritoryScope();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Close on click outside or Escape
  useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent) {
      const t = e.target as Node;
      if (popoverRef.current?.contains(t)) return;
      if (buttonRef.current?.contains(t)) return;
      setOpen(false);
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  // Reset search when reopening
  useEffect(() => {
    if (open) setSearch('');
  }, [open]);

  const allowedAfs = useMemo(() => {
    if (!user) return null;
    if (user.role === 'admin') return null;
    // FBCs default to their own territories but can opt into the whole network
    // via the "View only my territories" toggle. Plain franchisees stay scoped.
    if (user.role === 'fbc' && !onlyMyTerritories) return null;
    return new Set(user.afNumbers ?? []);
  }, [user, onlyMyTerritories]);

  const sortedFranchisees = useMemo(() => {
    const list = franchisees ?? [];
    let filtered = allowedAfs
      ? list.filter((f) => allowedAfs.has(f.af_number))
      : list;
    if (!showSatellites) {
      filtered = filtered.filter((f) => f.has_own_office !== false);
    }
    return filtered
      .slice()
      .sort((a, b) => a.af_number.localeCompare(b.af_number, undefined, { numeric: true }));
  }, [franchisees, allowedAfs, showSatellites]);

  // Map parent AF → the satellites folded under it (share its office). Drives
  // the "+N" badge on the parent row (with the satellite AF#s listed on hover)
  // and lets a satellite's AF#/name match the parent row in search, so the
  // satellites stay findable without adding rows of their own. Empty when
  // satellites are already shown as their own rows.
  const sharedSatellitesByParent = useMemo(() => {
    const m = new Map<string, Franchisee[]>();
    if (showSatellites) return m;
    for (const f of franchisees ?? []) {
      if (f.is_satellite && f.has_own_office === false && f.parent_af) {
        const arr = m.get(f.parent_af);
        if (arr) arr.push(f);
        else m.set(f.parent_af, [f]);
      }
    }
    for (const arr of m.values()) {
      arr.sort((a, b) => a.af_number.localeCompare(b.af_number, undefined, { numeric: true }));
    }
    return m;
  }, [franchisees, showSatellites]);

  const fbcs = useMemo(() => {
    if (user?.role !== 'admin') return [];
    return (fbcPerf ?? []).slice().sort((a, b) => a.fbcName.localeCompare(b.fbcName));
  }, [fbcPerf, user?.role]);

  const isLoading = fLoading || (user?.role === 'admin' && fbcLoading);

  // Franchisees with at most one assigned AF have nothing to switch between —
  // the picker would be a single non-actionable item. Hide it entirely.
  if (user?.role === 'franchisee' && (user.afNumbers?.length ?? 0) <= 1) {
    return null;
  }

  function commit(next: ContextView) {
    onChange(next);
    setOpen(false);
  }

  const buttonText = user
    ? viewLabel(view, user.role, franchisees ?? [])
    : 'Loading…';

  return (
    <div className="ctx-picker">
      <button
        ref={buttonRef}
        type="button"
        className={`ctx-trigger ${open ? 'is-open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        disabled={isLoading}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="ctx-trigger-label">View:</span>
        <span className="ctx-trigger-value">{buttonText}</span>
        <svg
          className="ctx-trigger-caret"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <>
          <div className="ctx-backdrop" onClick={() => setOpen(false)} />
          <div ref={popoverRef} className="ctx-popover" role="listbox">
            <Command label="Switch view" loop shouldFilter filter={strictFilter}>
              <Command.Input
                value={search}
                onValueChange={setSearch}
                placeholder="Search AF# or name..."
                autoFocus
                className="ctx-search"
              />
              {user?.role === 'fbc' && (
                <label className="ctx-satellite-toggle">
                  <input
                    type="checkbox"
                    checked={onlyMyTerritories}
                    onChange={(e) => setOnlyMyTerritories(e.target.checked)}
                  />
                  <span>View only my territories</span>
                </label>
              )}
              <label className="ctx-satellite-toggle">
                <input
                  type="checkbox"
                  checked={showSatellites}
                  onChange={(e) => setShowSatellites(e.target.checked)}
                />
                <span>Show additional territories sharing an office</span>
              </label>
              <Command.List className="ctx-list">
                <Command.Empty className="ctx-empty">No matches</Command.Empty>

                {(user?.role === 'admin' || user?.role === 'fbc') && (
                  <Command.Group
                    heading={user?.role === 'admin' ? 'Network' : 'My Portfolio'}
                    className="ctx-group"
                  >
                    <Command.Item
                      value={user?.role === 'admin' ? 'Network Overview' : 'My Portfolio'}
                      onSelect={() => commit({ kind: 'network' })}
                      className="ctx-item"
                    >
                      <span className="ctx-item-marker">★</span>
                      <span className="ctx-item-label">
                        {user?.role === 'admin' ? 'Network Overview' : 'My Portfolio'}
                      </span>
                    </Command.Item>
                  </Command.Group>
                )}

                {user?.role === 'admin' && fbcs.length > 0 && (
                  <Command.Group heading="FBC Portfolios" className="ctx-group">
                    {fbcs.map((fbc) => (
                      <Command.Item
                        key={fbc.fbcName}
                        value={`fbc ${fbc.fbcName}`}
                        keywords={[fbc.fbcName]}
                        onSelect={() => commit({ kind: 'fbc', fbcName: fbc.fbcName })}
                        className="ctx-item"
                      >
                        <span className="ctx-item-label">{fbc.fbcName}</span>
                        <span className="ctx-item-meta">{fbc.franchiseeCount} franchisees</span>
                      </Command.Item>
                    ))}
                  </Command.Group>
                )}

                <Command.Group
                  heading={
                    user?.role === 'admin' || user?.role === 'fbc'
                      ? `Franchisees (${sortedFranchisees.length})`
                      : 'Franchisees'
                  }
                  className="ctx-group"
                >
                  {sortedFranchisees.map((f) => {
                    const label = franchiseeLabel(f);
                    const sats = sharedSatellitesByParent.get(f.af_number) ?? [];
                    return (
                      <Command.Item
                        key={f.af_number}
                        value={`${f.af_number} ${label}`}
                        keywords={[
                          f.af_number,
                          f.marketing_name ?? '',
                          f.territory_name ?? '',
                          f.city ?? '',
                          f.state ?? '',
                          f.first_name ?? '',
                          f.last_name ?? '',
                          // Fold satellite AF#s/names in so searching them
                          // surfaces this parent row (satellites get no rows of
                          // their own; selecting opens the parent dashboard).
                          ...sats.flatMap((s) => [
                            s.af_number,
                            s.territory_name ?? '',
                            s.marketing_name ?? '',
                          ]),
                        ]}
                        onSelect={() =>
                          commit({ kind: 'franchisee', afNumber: f.af_number })
                        }
                        className="ctx-item"
                      >
                        <span className="ctx-item-af">{f.af_number}</span>
                        <span className="ctx-item-label">
                          {f.marketing_name ||
                            f.territory_name ||
                            [f.first_name, f.last_name].filter(Boolean).join(' ') ||
                            '(unnamed)'}
                          {sats.length > 0 && (
                            <span
                              className="ctx-item-territory-badge"
                              title={`Also at this office (opens this dashboard):\n${sats
                                .map((s) => `${s.af_number} — ${s.territory_name || s.marketing_name || ''}`.trim())
                                .join('\n')}`}
                            >
                              +{sats.length} territor{sats.length === 1 ? 'y' : 'ies'}
                            </span>
                          )}
                        </span>
                        {f.city && (
                          <span className="ctx-item-meta">
                            {f.city}
                            {f.state ? `, ${f.state}` : ''}
                          </span>
                        )}
                      </Command.Item>
                    );
                  })}
                </Command.Group>
              </Command.List>
            </Command>
          </div>
        </>
      )}
    </div>
  );
}
